import { clsx } from 'clsx';
import { ReactNode } from 'react';

import Icon from './Icon';

type Color = 'error' | 'gray' | 'info' | 'success';

const BACKGROUND_COLORS: Record<Color, string> = {
  error: 'text-error-d-700 border-error-d-300 bg-error-d-25',
  gray: 'text-gray-d-700 border-gray-d-300 bg-gray-d-25',
  info: 'border-[#FDE8D8] bg-[#FFFBF8]',
  success: 'border-[#C5DEDD] bg-[#F0F7F6]',
};

const Alert = ({
  children,
  color = 'error',
  supportingText,
}: {
  children: string | string[] | ReactNode;
  color?: Color;
  supportingText?: ReactNode;
}) => {
  const iconToRender = (() => {
    switch (color) {
      case 'success':
        return (
          <div className="absolute">
            <SuccessIcon />
          </div>
        );
      case 'info':
        return (
          <div className="absolute">
            <InfoIcon />
          </div>
        );
      case 'error':
      case 'gray':
      default:
        return (
          <div className="absolute -translate-y-1/4">
            <IconWithRings color={color} />
          </div>
        );
    }
  })();

  return (
    <div
      className={clsx(
        'relative p-4 flex gap-2 items-start rounded-xl border shadow-sm',
        BACKGROUND_COLORS[color],
      )}
    >
      <div className="absolute -translate-y-1/4">{iconToRender}</div>

      <div className="text-sm space-y-1 pl-12">
        <p className="font-semibold">{children}</p>

        {supportingText}
      </div>
    </div>
  );
};

export default Alert;

const ICON_BORDER_COLORS = {
  error: 'border-error-d-600',
  gray: 'border-gray-d-600',
  info: 'border-[#E8693A]',
  success: 'border-[#C5DEDD]',
};
const ICON_TEXT_COLORS = {
  error: 'text-error-d-600',
  gray: 'text-gray-d-600',
  info: 'text-[#E8693A]',
  success: 'text-[#C5DEDD]',
};

const IconWithRings = ({ color }: { color: Color }) => {
  return (
    <div
      className={clsx(
        'relative flex items-center justify-center w-9 h-9',
        ICON_TEXT_COLORS[color],
      )}
    >
      {/* Outer ring */}
      <div
        className={clsx(
          'absolute inset-0 w-9 h-9 border opacity-10 rounded-full',
          ICON_BORDER_COLORS[color],
        )}
      />
      {/* Inner ring */}
      <div
        className={clsx(
          'absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-7 h-7 border opacity-30 rounded-full',
          ICON_BORDER_COLORS[color],
        )}
      />

      <div className="w-5 h-5">
        <Icon id="alert-circle" />
      </div>
    </div>
  );
};

const SuccessIcon = () => (
  <div className="flex w-5 h-5 rounded-full bg-[#1A3836] justify-center items-center">
    <div className="w-3 h-3 text-white">
      <Icon id="check" />
    </div>
  </div>
);

const InfoIcon = () => {
  return (
    <div className="w-5 h-5 text-[#E8693A]">
      <Icon id="info-circle" />
    </div>
  );
};
